import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import ExportButton from "../analytics/components/ExportButton";
import StudioDateInput from "../../components/ui/StudioDateInput";
import { useNotification } from "../../context/NotificationContext";
import {
    getUsageDecline,
    UsageDeclineAppHubCompany,
    UsageDeclineCategory,
    UsageDeclineMetric,
    UsageDeclineMode,
    UsageDeclineOverviewCompany,
    UsageDeclineResponse,
    UsageDeclineStudioCompany,
} from "../../api/support";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import studio from "../../styles/studioPage.module.css";
import styles from "./UsageDecline.module.css";

type TabId = "overview" | "appHub" | "studio";
type SortMode = "priority" | "issuesPct" | "absoluteDrop" | "mostActive";

const formatDeltaPct = (pct: number | null): string => {
    if (pct === null || pct === undefined) return "—";
    const sign = pct > 0 ? "+" : "";
    return `${sign}${pct.toFixed(1)}%`;
};

const deltaClass = (pct: number | null): string => {
    if (pct === null || pct === undefined || Math.abs(pct) < 0.05) return styles.deltaFlat;
    if (pct < -40) return styles.deltaDownSevere;
    if (pct < 0) return styles.deltaDown;
    return styles.deltaUp;
};

const formatMetric = (m: UsageDeclineMetric) => (
    <span className={styles.metricCell}>
        {m.current} / {m.previous}{" "}
        <span className={deltaClass(m.deltaPct)}>
            ({m.delta >= 0 ? "+" : ""}
            {m.delta}, {formatDeltaPct(m.deltaPct)})
        </span>
    </span>
);

const BadgeWithTip: React.FC<{ tip: string; className: string; children: React.ReactNode }> = ({
    tip,
    className,
    children,
}) => (
    <Tooltip title={tip} arrow placement="top">
        <span className={className} tabIndex={0}>
            {children}
        </span>
    </Tooltip>
);

const isOverview = (c: UsageDeclineResponse["companies"][number]): c is UsageDeclineOverviewCompany =>
    "appHub" in c && "studio" in c;

const isAppHub = (c: UsageDeclineResponse["companies"][number]): c is UsageDeclineAppHubCompany =>
    "trackStarts" in c && !("appHub" in c);

const isStudio = (c: UsageDeclineResponse["companies"][number]): c is UsageDeclineStudioCompany =>
    "jobs" in c && !("studio" in c);

const UsageDecline: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    const [tab, setTab] = useState<TabId>("overview");
    const [mode, setMode] = useState<UsageDeclineMode>("30d");
    const [sort, setSort] = useState<SortMode>("priority");
    const [issuesOnly, setIssuesOnly] = useState(true);
    const [showLowBaseline, setShowLowBaseline] = useState(false);
    const [search, setSearch] = useState("");
    const [currentFrom, setCurrentFrom] = useState("");
    const [currentTo, setCurrentTo] = useState("");
    const [previousFrom, setPreviousFrom] = useState("");
    const [previousTo, setPreviousTo] = useState("");
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<UsageDeclineResponse | null>(null);

    const category: UsageDeclineCategory = tab;

    const fetchData = useCallback(async () => {
        const token = localStorage.getItem("token");
        if (!token) return;

        if (mode === "custom") {
            if (!currentFrom || !currentTo || !previousFrom || !previousTo) {
                return;
            }
        }

        try {
            setLoading(true);
            const response = await getUsageDecline({
                token,
                mode,
                category,
                includeLowBaseline: showLowBaseline,
                ...(mode === "custom" ? { currentFrom, currentTo, previousFrom, previousTo } : {}),
            });
            setData(response);
        } catch (error) {
            console.error(error);
            showNotification({
                message: t("usageDeclineFetchError") || "Error fetching usage decline data",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }, [mode, category, showLowBaseline, currentFrom, currentTo, previousFrom, previousTo, showNotification, t]);

    useEffect(() => {
        if (tab === "overview") setSort("priority");
        else setSort("issuesPct");
    }, [tab]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredSorted = useMemo(() => {
        if (!data) return [];
        let rows = [...data.companies];

        const q = search.trim().toLowerCase();
        if (q) {
            rows = rows.filter((r) => r.name.toLowerCase().includes(q));
        }

        if (!showLowBaseline) {
            rows = rows.filter((r) => !r.lowBaseline);
        }

        if (issuesOnly) {
            rows = rows.filter((r) => r.isIssue);
        }

        rows.sort((a, b) => {
            if (sort === "priority") {
                return (b.priorityScore || 0) - (a.priorityScore || 0);
            }
            if (sort === "mostActive") {
                return (b.combinedCurrent || 0) - (a.combinedCurrent || 0);
            }
            if (sort === "absoluteDrop") {
                const dropA = (a.combinedPrevious || 0) - (a.combinedCurrent || 0);
                const dropB = (b.combinedPrevious || 0) - (b.combinedCurrent || 0);
                return dropB - dropA;
            }
            // issuesPct — worst % first
            const pctA = isOverview(a)
                ? Math.min(a.appHub.combinedDeltaPct ?? 0, a.studio.combinedDeltaPct ?? 0)
                : ((a as UsageDeclineAppHubCompany | UsageDeclineStudioCompany).combinedDeltaPct ?? 0);
            const pctB = isOverview(b)
                ? Math.min(b.appHub.combinedDeltaPct ?? 0, b.studio.combinedDeltaPct ?? 0)
                : ((b as UsageDeclineAppHubCompany | UsageDeclineStudioCompany).combinedDeltaPct ?? 0);
            return pctA - pctB;
        });

        return rows;
    }, [data, search, showLowBaseline, issuesOnly, sort]);

    const exportCsv = () => {
        if (!filteredSorted.length) return;
        const lines: string[] = [];

        if (tab === "overview") {
            lines.push(
                [
                    "company",
                    "priorityScore",
                    "isIssue",
                    "wentDark",
                    "bothDeclining",
                    "appDeltaPct",
                    "appDelta",
                    "studioDeltaPct",
                    "studioDelta",
                    "lastActivityAt",
                ].join(","),
            );
            for (const row of filteredSorted) {
                if (!isOverview(row)) continue;
                lines.push(
                    [
                        JSON.stringify(row.name),
                        row.priorityScore,
                        row.isIssue,
                        row.wentDark,
                        row.bothDeclining,
                        row.appHub.combinedDeltaPct ?? "",
                        row.appHub.combinedDelta,
                        row.studio.combinedDeltaPct ?? "",
                        row.studio.combinedDelta,
                        row.lastActivityAt ?? "",
                    ].join(","),
                );
            }
        } else if (tab === "appHub") {
            lines.push(
                "company,trackStartsCur,trackStartsPrev,hubCur,hubPrev,combinedDeltaPct,isIssue,wentDark,lastActivityAt",
            );
            for (const row of filteredSorted) {
                if (!isAppHub(row)) continue;
                lines.push(
                    [
                        JSON.stringify(row.name),
                        row.trackStarts.current,
                        row.trackStarts.previous,
                        row.hubUniques.current,
                        row.hubUniques.previous,
                        row.combinedDeltaPct ?? "",
                        row.isIssue,
                        row.wentDark,
                        row.lastActivityAt ?? "",
                    ].join(","),
                );
            }
        } else {
            lines.push(
                "company,jobsCur,jobsPrev,adminsCur,adminsPrev,charsCur,charsPrev,combinedDeltaPct,isIssue,wentDark,lastActivityAt",
            );
            for (const row of filteredSorted) {
                if (!isStudio(row)) continue;
                lines.push(
                    [
                        JSON.stringify(row.name),
                        row.jobs.current,
                        row.jobs.previous,
                        row.activeAdmins.current,
                        row.activeAdmins.previous,
                        row.chars.current,
                        row.chars.previous,
                        row.combinedDeltaPct ?? "",
                        row.isIssue,
                        row.wentDark,
                        row.lastActivityAt ?? "",
                    ].join(","),
                );
            }
        }

        const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `usage-decline-${tab}-${data?.period.current.from || "export"}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const summary = data?.summary;

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle
                    title={t("usageDecline") || "Usage decline"}
                    subtitle={
                        t("usageDeclineSubtitle") ||
                        "Owner-only CSM queue — spot companies that need outreach and who’s most active."
                    }
                />
            </div>

            <div className={styles.tabs}>
                {(
                    [
                        ["overview", t("usageDeclineNeedsSupport") || "Needs support"],
                        ["appHub", t("usageDeclineAppHub") || "App & Hub"],
                        ["studio", t("usageDeclineStudio") || "Studio"],
                    ] as const
                ).map(([id, label]) => (
                    <button
                        key={id}
                        type="button"
                        className={tab === id ? styles.tabActive : styles.tab}
                        onClick={() => setTab(id)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className={styles.controls}>
                <div className={styles.controlGroup}>
                    <span className={styles.controlLabel}>{t("usageDeclinePeriod") || "Period"}</span>
                    <select
                        className={styles.select}
                        value={mode}
                        onChange={(e) => setMode(e.target.value as UsageDeclineMode)}
                    >
                        <option value="30d">{t("usageDecline30d") || "Last 30 days"}</option>
                        <option value="7d">{t("usageDecline7d") || "Last 7 days"}</option>
                        <option value="yoy">{t("usageDeclineYoy") || "Year over year (30d)"}</option>
                        <option value="custom">{t("usageDeclineCustom") || "Custom"}</option>
                    </select>
                </div>

                <div className={styles.controlGroup}>
                    <span className={styles.controlLabel}>{t("usageDeclineSort") || "Sort"}</span>
                    <select
                        className={styles.select}
                        value={sort}
                        onChange={(e) => setSort(e.target.value as SortMode)}
                    >
                        {tab === "overview" && (
                            <option value="priority">{t("usageDeclineSortPriority") || "Priority"}</option>
                        )}
                        <option value="issuesPct">{t("usageDeclineSortIssues") || "Issues %"}</option>
                        <option value="absoluteDrop">{t("usageDeclineSortAbsolute") || "Absolute drop"}</option>
                        <option value="mostActive">{t("usageDeclineSortActive") || "Most active"}</option>
                    </select>
                </div>

                <div className={styles.controlGroup}>
                    <span className={styles.controlLabel}>{t("search") || "Search"}</span>
                    <input
                        className={styles.textInput}
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("usageDeclineSearchPlaceholder") || "Company name"}
                    />
                </div>

                <div className={styles.controlRow}>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={issuesOnly}
                            onChange={(e) => setIssuesOnly(e.target.checked)}
                        />
                        {t("usageDeclineIssuesOnly") || "Issues only"}
                    </label>
                    <label className={styles.checkboxLabel}>
                        <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={showLowBaseline}
                            onChange={(e) => setShowLowBaseline(e.target.checked)}
                        />
                        {t("usageDeclineShowLowBaseline") || "Show low baseline"}
                    </label>
                    <ExportButton
                        label={t("exportCsv") || "Export CSV"}
                        onClick={exportCsv}
                        disabled={!filteredSorted.length}
                    />
                </div>

                {mode === "custom" && (
                    <div className={styles.customDates}>
                        <div className={`${styles.controlGroup} ${styles.dateField}`}>
                            <span className={styles.controlLabel}>
                                {t("usageDeclineCurrentFrom") || "Current from"}
                            </span>
                            <StudioDateInput value={currentFrom} onChange={setCurrentFrom} />
                        </div>
                        <div className={`${styles.controlGroup} ${styles.dateField}`}>
                            <span className={styles.controlLabel}>{t("usageDeclineCurrentTo") || "Current to"}</span>
                            <StudioDateInput value={currentTo} onChange={setCurrentTo} />
                        </div>
                        <div className={`${styles.controlGroup} ${styles.dateField}`}>
                            <span className={styles.controlLabel}>
                                {t("usageDeclinePreviousFrom") || "Previous from"}
                            </span>
                            <StudioDateInput value={previousFrom} onChange={setPreviousFrom} />
                        </div>
                        <div className={`${styles.controlGroup} ${styles.dateField}`}>
                            <span className={styles.controlLabel}>{t("usageDeclinePreviousTo") || "Previous to"}</span>
                            <StudioDateInput value={previousTo} onChange={setPreviousTo} />
                        </div>
                    </div>
                )}
            </div>

            <p className={styles.readGuide}>
                <strong>{t("usageDeclineHowToReadTitle") || "How to read this"}:</strong>{" "}
                {t("usageDeclineHowToRead") ||
                    "Priority ranks App/Hub drops first (visitors & track starts). Studio is secondary — quiet Studio after content creation is normal. Call companies at the top; use badges for context, not every red number."}
            </p>

            {data?.period && (
                <p className={styles.periodHint}>
                    {t("usageDeclineComparing") || "Comparing"} {data.period.current.from} → {data.period.current.to}{" "}
                    {t("usageDeclineVs") || "vs"} {data.period.previous.from} → {data.period.previous.to}
                </p>
            )}

            {summary && (
                <div className={styles.summary}>
                    <span className={styles.summaryItem}>
                        <strong>{summary.needSupportCount}</strong> {t("usageDeclineNeedSupport") || "need support"}
                    </span>
                    <span className={styles.summaryItem}>
                        <strong>{summary.wentDarkCount}</strong> {t("usageDeclineWentDark") || "went dark"}
                    </span>
                    {tab === "overview" && (
                        <span className={styles.summaryItem}>
                            <strong>{summary.bothDecliningCount}</strong>{" "}
                            {t("usageDeclineBothDeclining") || "both declining"}
                        </span>
                    )}
                    {summary.topActive && (
                        <span className={styles.summaryItem}>
                            {t("usageDeclineMostActive") || "Most active"}:{" "}
                            <strong>
                                {summary.topActive.name} ({summary.topActive.volume})
                            </strong>
                        </span>
                    )}
                </div>
            )}

            {loading ? (
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                </div>
            ) : filteredSorted.length === 0 ? (
                <div className={`${adminSurface.tableContainer} ${adminSurface.tblScrollSupport}`}>
                    <div className={styles.empty}>{t("usageDeclineEmpty") || "No companies match these filters."}</div>
                </div>
            ) : (
                <div className={`${adminSurface.tableContainer} ${adminSurface.tblScrollSupport}`}>
                    <table className={adminSurface.table}>
                        <thead>
                            {tab === "overview" && (
                                <tr>
                                    <th>{t("company") || "Company"}</th>
                                    <th>{t("usageDeclinePriority") || "Priority"}</th>
                                    <th>{t("usageDeclineBadges") || "Badges"}</th>
                                    <th>{t("usageDeclineAppHub") || "App & Hub"}</th>
                                    <th>
                                        {t("usageDeclineStudio") || "Studio"}{" "}
                                        <span className={styles.mutedNote}>
                                            ({t("usageDeclineStudioSecondary") || "secondary"})
                                        </span>
                                    </th>
                                    <th>{t("usageDeclineLastActivity") || "Last activity"}</th>
                                    <th>{t("actions") || "Actions"}</th>
                                </tr>
                            )}
                            {tab === "appHub" && (
                                <tr>
                                    <th>{t("company") || "Company"}</th>
                                    <th>{t("usageDeclineTrackStarts") || "Track starts"}</th>
                                    <th>{t("usageDeclineHubUniques") || "Hub unique visitors"}</th>
                                    <th>{t("usageDeclineCombined") || "Combined"}</th>
                                    <th>{t("usageDeclineLastActivity") || "Last activity"}</th>
                                    <th>{t("actions") || "Actions"}</th>
                                </tr>
                            )}
                            {tab === "studio" && (
                                <tr>
                                    <th>{t("company") || "Company"}</th>
                                    <th>{t("usageDeclineJobs") || "QueueAI jobs"}</th>
                                    <th>{t("usageDeclineActiveAdmins") || "Active admins"}</th>
                                    <th>{t("usageDeclineChars") || "Chars"}</th>
                                    <th>{t("usageDeclineLastActivity") || "Last activity"}</th>
                                    <th>{t("actions") || "Actions"}</th>
                                </tr>
                            )}
                        </thead>
                        <tbody>
                            {filteredSorted.map((row) => {
                                if (tab === "overview" && isOverview(row)) {
                                    return (
                                        <tr key={row.companyId} className={row.isIssue ? styles.issueRow : undefined}>
                                            <td>{row.name}</td>
                                            <td className={styles.metricCell}>{row.priorityScore}</td>
                                            <td>
                                                <div className={styles.badges}>
                                                    {row.wentDark && (
                                                        <BadgeWithTip
                                                            className={styles.badgeDark}
                                                            tip={t("usageDeclineBadgeWentDarkTip", {
                                                                prev: row.appHub.combinedPrevious,
                                                                cur: row.appHub.combinedCurrent,
                                                            })}
                                                        >
                                                            {t("usageDeclineBadgeWentDark") || "Went dark"}
                                                        </BadgeWithTip>
                                                    )}
                                                    {row.bothDeclining && (
                                                        <BadgeWithTip
                                                            className={styles.badgeBoth}
                                                            tip={t("usageDeclineBadgeBothTip", {
                                                                appPrev: row.appHub.combinedPrevious,
                                                                appCur: row.appHub.combinedCurrent,
                                                                appPct: formatDeltaPct(row.appHub.combinedDeltaPct),
                                                                studioPrev: row.studio.combinedPrevious,
                                                                studioCur: row.studio.combinedCurrent,
                                                                studioPct: formatDeltaPct(row.studio.combinedDeltaPct),
                                                            })}
                                                        >
                                                            {t("usageDeclineBadgeBoth") || "Both declining"}
                                                        </BadgeWithTip>
                                                    )}
                                                    {row.appHub.isIssue && !row.bothDeclining && (
                                                        <BadgeWithTip
                                                            className={styles.badgeApp}
                                                            tip={t("usageDeclineBadgeAppTip", {
                                                                prev: row.appHub.combinedPrevious,
                                                                cur: row.appHub.combinedCurrent,
                                                                pct: formatDeltaPct(row.appHub.combinedDeltaPct),
                                                            })}
                                                        >
                                                            {t("usageDeclineBadgeApp") || "App issue"}
                                                        </BadgeWithTip>
                                                    )}
                                                    {row.studio.isIssue && !row.bothDeclining && (
                                                        <BadgeWithTip
                                                            className={styles.badgeStudio}
                                                            tip={t("usageDeclineBadgeStudioSecondaryTip", {
                                                                prev: row.studio.combinedPrevious,
                                                                cur: row.studio.combinedCurrent,
                                                                pct: formatDeltaPct(row.studio.combinedDeltaPct),
                                                            })}
                                                        >
                                                            {t("usageDeclineBadgeStudioSecondary") ||
                                                                "Studio also down"}
                                                        </BadgeWithTip>
                                                    )}
                                                    {row.isGrowing && !row.isIssue && (
                                                        <BadgeWithTip
                                                            className={styles.badgeGrowing}
                                                            tip={t("usageDeclineBadgeGrowingTip", {
                                                                prev: row.appHub.combinedPrevious,
                                                                cur: row.appHub.combinedCurrent,
                                                                pct: formatDeltaPct(row.appHub.combinedDeltaPct),
                                                            })}
                                                        >
                                                            {t("usageDeclineBadgeGrowing") || "Growing"}
                                                        </BadgeWithTip>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className={deltaClass(row.appHub.combinedDeltaPct)}>
                                                    {row.appHub.combinedDelta >= 0 ? "+" : ""}
                                                    {row.appHub.combinedDelta} /{" "}
                                                    {formatDeltaPct(row.appHub.combinedDeltaPct)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={deltaClass(row.studio.combinedDeltaPct)}>
                                                    {row.studio.combinedDelta >= 0 ? "+" : ""}
                                                    {row.studio.combinedDelta} /{" "}
                                                    {formatDeltaPct(row.studio.combinedDeltaPct)}
                                                </span>
                                            </td>
                                            <td>
                                                {row.lastActivityAt
                                                    ? new Date(row.lastActivityAt).toLocaleString()
                                                    : "—"}
                                            </td>
                                            <td>
                                                <div className={styles.actions}>
                                                    <Link className={styles.actionLink} to="/analytics">
                                                        {t("analytics") || "Analytics"}
                                                    </Link>
                                                    <Link
                                                        className={styles.actionLink}
                                                        to={`/support/queue-ai-logs?companyId=${encodeURIComponent(row.companyId)}`}
                                                    >
                                                        {t("queueAILogs") || "QueueAI"}
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                }

                                if (tab === "appHub" && isAppHub(row)) {
                                    return (
                                        <tr key={row.companyId} className={row.isIssue ? styles.issueRow : undefined}>
                                            <td>{row.name}</td>
                                            <td>{formatMetric(row.trackStarts)}</td>
                                            <td>{formatMetric(row.hubUniques)}</td>
                                            <td>
                                                <span className={styles.metricCell}>
                                                    {row.combinedCurrent} / {row.combinedPrevious}{" "}
                                                    <span className={deltaClass(row.combinedDeltaPct)}>
                                                        ({formatDeltaPct(row.combinedDeltaPct)})
                                                    </span>
                                                </span>
                                            </td>
                                            <td>
                                                {row.lastActivityAt
                                                    ? new Date(row.lastActivityAt).toLocaleString()
                                                    : "—"}
                                            </td>
                                            <td>
                                                <Link className={styles.actionLink} to="/analytics">
                                                    {t("analytics") || "Analytics"}
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                }

                                if (tab === "studio" && isStudio(row)) {
                                    return (
                                        <tr key={row.companyId} className={row.isIssue ? styles.issueRow : undefined}>
                                            <td>{row.name}</td>
                                            <td>{formatMetric(row.jobs)}</td>
                                            <td>{formatMetric(row.activeAdmins)}</td>
                                            <td>{formatMetric(row.chars)}</td>
                                            <td>
                                                {row.lastActivityAt
                                                    ? new Date(row.lastActivityAt).toLocaleString()
                                                    : "—"}
                                            </td>
                                            <td>
                                                <Link
                                                    className={styles.actionLink}
                                                    to={`/support/queue-ai-logs?companyId=${encodeURIComponent(row.companyId)}`}
                                                >
                                                    {t("queueAILogs") || "QueueAI"}
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                }

                                return null;
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default UsageDecline;
